import { validate as isValidUUID } from 'uuid';
import config from '../config/index';
import { getTwitchUsernameForLeviathanStream } from './entity';
import {
    BINGO_LICENSE,
    BINGO_NAME,
    LEVIATHAN_LICENSE,
    LEVIATHAN_NAME,
    MARBLES_LICENSE,
    MARBLES_NAME,
    RACE_DAY_LICENSE,
    RACE_DAY_NAME,
} from './games';
import { UserProfile } from './social';
import { PaginatedData } from './util';

const LogPrefix = 'lobby';

interface ActorInfoResponse {
    type: 'success' | 'error';
    data?: {
        activePlayerCount?: number;
        isActive?: boolean;
        [k: string]: any;
    }; // payload from actor if successful
    message?: string; // if error
}

interface UserFilter {
    userFilter?: {
        apple?: string[];
        facebook?: string[];
        google?: string[];
        twitch?: string[];
        streamsix?: string[];
    };
    roomIds?: string[];
}

enum RoomState {
    'active',
    'started',
    'deleted',
}

export interface GameInfo {
    id: string;
    state?: RoomState;
    joinCode: string;
    owner: string;
    idpIds?: Map<string, string>;
    createdAt: string;
    updatedAt: string;
    name: string;
    sessionId: string;
    gameserverState: string;
    maxPlayers: number;
    joinable: number;
    rejoinable: number;
    localRmb?: number;
    branchName?: string;
    rmbTag?: string;
    actorTag?: string;
    actorName?: string;
    license: string;
    actorInfo?: ActorInfoResponse;
    userProfile?: UserProfile;
}

export interface GetRoomsResponse {
    data: GameInfo[];
    page: PaginatedData;
}

export interface RoomsStats {
    totalSessions: number;
    totalPlayers: number;
}

export enum SortAttribute {
    NumViewers = 'num-viewers',
    NumberPlayers = 'num-players',
    StartTime = 'start-time',
}

export enum SortDirection {
    ASC,
    DESC,
}

export interface SortingOption {
    /** The attribute of the rooms to sort by */
    attribute: SortAttribute;
    /** Can be left out. The backend will assume a default of SortDirection.DESC */
    direction?: SortDirection;
    /** defaults to -inf */
    minValue?: string;
    /** defaults to inf */
    maxValue?: string;
}

export enum PlatformOption {
    Twitch = 'twitch',
}

export interface FilterOption {
    state?: RoomState;
    platform?: PlatformOption;
}

const sortParamToQueryParam = (value: SortingOption): string[][] => [
    ['sort_by', value.attribute],
    [
        'sort_attrs',
        [
            value.minValue || '-inf',
            value.maxValue || 'inf',
            ...(value.direction !== undefined ? [SortDirection[value.direction]] : []),
        ].join(','),
    ],
];

/**
 * If an argument for pagis supplied and isn't undefined then the other params will be ignored.
 *
 * @param license the game license
 * @param sortParams the params to sort by. **NOTE**: Only the first sort attribute will be used to sort the rooms. All of the sorting options after that is used to bound the results.
 * @param platform optionally filter which platform the user is live on
 * @param page a string representing the query params from the last call and a cursor to the point in the list of pages
 */
export const getRunningGames = async (
    sortParams: SortingOption[],
    license?: string,
    platform?: PlatformOption,
    page?: string,
): Promise<GetRoomsResponse | null> => {
    const logPrefix = `[${LogPrefix}.getRunningGames]`;

    const query = page
        ? page
        : new URLSearchParams([
              ['state', 'active'],
              ['limit', `${config.lobbyQueryLimit}`],
              ...(sortParams?.map(sortParamToQueryParam).flat() || []),
              ...(license ? [['license', license]] : []),
              ...(platform ? [['platform', platform]] : []),
          ]);

    const url = `${config.lobbyServiceURL}/rooms?${query}`;
    const resp = await fetch(url, {
        headers: {
            'Content-Type': 'application/json',
        },
        method: 'GET',
    });

    if (resp.status !== 200) {
        console.error(`${logPrefix} url: ${url} error: ${await resp.text()}`);
        return null;
    }

    return await resp.json();
};

export const getRoomsByUserFilter = async (
    userFilter: UserFilter,
    signal?: AbortSignal,
): Promise<GameInfo[] | undefined> => {
    const logPrefix = `[${LogPrefix}.getRoomsByUserFilter]`;

    const url = `${config.lobbyServiceURL}/rooms/filter`;
    const resp = await fetch(url, {
        headers: {
            'Content-Type': 'application/json',
        },
        method: 'POST',
        body: JSON.stringify(userFilter),
        signal: signal,
    });

    if (resp.status !== 200) {
        console.error(`${logPrefix} url: ${url} error: ${await resp.text()}`);
        return undefined;
    }

    const data = await resp.json();
    const lobby = data.data;
    return lobby;
};

export const getRoomsStats = async (
    license: string,
    signal?: AbortSignal,
): Promise<RoomsStats | undefined> => {
    const logPrefix = `[${LogPrefix}.getRoomsStats]`;

    let url = `${config.lobbyServiceURL}/rooms/stats`;
    if (license !== '') {
        url += `?license=${license}`;
    }
    const resp = await fetch(url, {
        headers: {
            'Content-Type': 'application/json',
        },
        method: 'GET',
        signal: signal,
    });

    if (resp.status !== 200) {
        console.error(`${logPrefix} url: ${url} error: ${await resp.text()}`);
        return undefined;
    }

    const data = await resp.json();
    return data.data;
};

export const getTwitchUsername = async (
    listItem: any,
    token: string,
): Promise<string | undefined> => {
    switch (listItem.license) {
        case MARBLES_LICENSE:
            return listItem.name;
        // @ts-ignore
        case LEVIATHAN_LICENSE:
            const twithUsername = await getTwitchUsernameForLeviathanStream(listItem.owner, token);
            if (twithUsername) {
                return twithUsername;
            }
    }
};

export const getUserName = (profile?: UserProfile, alternameUsername?: string): string => {
    if (!profile) {
        return '';
    }
    if (isValidUUID(profile.username)) {
        if (alternameUsername) {
            return alternameUsername;
        } else if (profile.twitch_profile) {
            return profile.twitch_profile.login;
        }
    }
    return profile.username;
};

export const getGameName = (license?: string) => {
    switch (license) {
        case LEVIATHAN_LICENSE:
            return LEVIATHAN_NAME;
        case MARBLES_LICENSE:
            return MARBLES_NAME;
        case RACE_DAY_LICENSE:
            return RACE_DAY_NAME;
        case BINGO_LICENSE:
            return BINGO_NAME;
        default:
            return '';
    }
};

const genericstringeamMessage = (gameName: string, username?: string) => {
    let message = `Come play ${gameName}`;
    if (username) {
        message += ` with ${username}`;
    }
    return message;
};

export const getStreamInfo = (gameInfo?: GameInfo, profile?: UserProfile) => {
    if (gameInfo !== undefined) {
        // invalid gameInfo, likely for offline user
        if (!gameInfo.license) {
            return '';
        }

        const gameName = getGameName(gameInfo.license);

        switch (gameInfo.license) {
            // @ts-ignore
            case LEVIATHAN_LICENSE:
                if (gameInfo.actorInfo?.type === 'success' && gameInfo.actorInfo.data?.gameMode) {
                    return `Game Mode: ${gameInfo.actorInfo.data.gameMode}`;
                }
                return `Game Mode: Unknown`;
            case MARBLES_LICENSE:
                return genericstringeamMessage(MARBLES_NAME, gameInfo.name);
            default:
                return genericstringeamMessage(gameName, getUserName(profile));
        }
    } else {
        return '';
    }
};
